import React, { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CatalogRecord, CompressorFields, Fields, FormValues } from "./CatalogAdmin.types.tsx";
import FieldInput from "./FieldInput.tsx";
import {
  COEFFICIENTS,
  failureMessages,
  normalizeModel,
  parseCoefficients,
  problemOf,
  toFields,
  toFormValues,
} from "./model.tsx";
import { COMPRESSOR_SCALARS } from "./resources.tsx";

import { createRecord, updateRecord } from "../../services/catalog.services.tsx";

const CURVES = ["rt_c", "rt_w", "rt_a", "rt_m"];

interface CompressorModalProps {
  record: CatalogRecord<CompressorFields> | null;
  initialModel?: string;
  refrigerants: string[];
  onHide: () => void;
  onSaved: () => void;
}

const CompressorModal: React.FC<CompressorModalProps> = ({
  record,
  initialModel,
  refrigerants,
  onHide,
  onSaved,
}) => {
  const { t } = useTranslation();
  const source = (record?.fields ?? null) as Fields | null;
  const [values, setValues] = useState<FormValues>(() => ({
    ...toFormValues(COMPRESSOR_SCALARS, source),
    ...(record ? {} : { model: initialModel ?? "" }),
  }));
  const [curves, setCurves] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      CURVES.map((curve) => [curve, ((source?.[curve] as number[] | null) ?? []).join(" ")])
    )
  );
  const [attempted, setAttempted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [messages, setMessages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const parsed = Object.fromEntries(
    CURVES.map((curve) => [curve, parseCoefficients(curves[curve])])
  );

  const complete = (curve: string) => {
    const { values: numbers, invalid } = parsed[curve];

    if (curve === "rt_m" && numbers.length === 0 && invalid.length === 0) {
      return true;
    }

    return invalid.length === 0 && numbers.length === COEFFICIENTS;
  };

  const model = values.model ?? "";
  const normalized = normalizeModel(model);
  const names = [...COMPRESSOR_SCALARS.map((field) => field.name), ...CURVES];

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);

    if (!CURVES.every(complete)) {
      return;
    }

    setBusy(true);

    try {
      const body = {
        ...toFields(COMPRESSOR_SCALARS, values),
        rt_c: parsed.rt_c.values,
        rt_w: parsed.rt_w.values,
        rt_a: parsed.rt_a.values,
        rt_m: parsed.rt_m.values.length === 0 ? null : parsed.rt_m.values,
      };

      if (record) {
        await updateRecord("compressors", record.id, body);
      } else {
        await createRecord("compressors", body);
      }

      onSaved();
    } catch (error) {
      setFieldErrors(problemOf(error).errors);
      setMessages(failureMessages(error, (key) => names.includes(key), t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered size="lg" className="catalog-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t(record ? "catalog.form.editTitle" : "catalog.form.newTitle", {
              resource: t("catalog.resources.compressors"),
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {messages.map((message) => (
            <Alert key={message} variant="danger">
              {message}
            </Alert>
          ))}

          {COMPRESSOR_SCALARS.map((field) => (
            <React.Fragment key={field.name}>
              <FieldInput
                scope="compressor"
                field={field}
                value={values[field.name] ?? ""}
                errors={fieldErrors[field.name]}
                list={field.name === "refrigerant" ? "compressor-refrigerants" : undefined}
                onChange={(value) => setValues({ ...values, [field.name]: value })}
              />
              {field.name === "model" && normalized !== "" && normalized !== model && (
                <Form.Text className="d-block mb-3">
                  {t("catalog.compressor.normalized", { model: normalized })}
                </Form.Text>
              )}
            </React.Fragment>
          ))}

          <datalist id="compressor-refrigerants">
            {refrigerants.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          <Form.Text className="d-block mb-2">{t("catalog.compressor.hint")}</Form.Text>

          {CURVES.map((curve) => (
            <Form.Group key={curve} className="mb-3" controlId={`compressor-${curve}`}>
              <Form.Label>{`${t(`catalog.fields.${curve}`)}${curve === "rt_m" ? "" : " *"}`}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={curves[curve]}
                isInvalid={Boolean(fieldErrors[curve]) || (attempted && !complete(curve))}
                onChange={(event) => setCurves({ ...curves, [curve]: event.target.value })}
              />
              <Form.Text>
                {t("catalog.compressor.count", {
                  n: parsed[curve].values.length,
                  total: COEFFICIENTS,
                })}
              </Form.Text>
              {parsed[curve].invalid.length > 0 && (
                <Form.Text className="d-block text-danger">
                  {t("catalog.compressor.invalid", { values: parsed[curve].invalid.join(" ") })}
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {fieldErrors[curve]?.join(" ")}
              </Form.Control.Feedback>
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t("catalog.form.cancel")}
          </Button>
          <Button type="submit" variant="success" disabled={busy}>
            {t("catalog.form.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CompressorModal;
